import { assign, fromPromise, setup } from 'xstate';

export function createStateMachine(initialValue: string, onSaveChanges: (value: string) => Promise<void>) {
    return setup({
        types: {
            context: {} as {
                originalValue: string;
                currentValue: string;
            },
            events: {} as
                | { type: 'Enter edit mode' }
                | { type: 'Exit edit mode' }
                | { type: 'Change value'; value: string }
                | { type: 'Save changes' },
        },
        actors: {
            saveChanges: fromPromise(({ input }: { input: string }) => {
                return onSaveChanges(input);
            }),
        },
        actions: {
            resetValue: assign({
                currentValue: ({ context }) => context.originalValue,
            }),
        },
        guards: {
            hasChanges: ({ context }, params: { value: string }) => {
                return params.value !== context.originalValue;
            },
        },
    }).createMachine({
        /** @xstate-layout N4IgpgJg5mDOIC5QEkB2AbAlqsAVMAHgC4CiEmRAdAEpgCGEA8hgJ4DEJqRYATgASQKfALYB7CGADaABgC6iUAAdRsCplGoFIAogCsAJgA0IFogDM0ygBYA7LoAcZ+wE5n+q-t1mAbN4C+fsZoWDj4xGQUlBFE2FCUAMLo9Khs8QAWdKgwfABudOgArlJyWsqqMRpaOggAjGZWlAbS9rZOztI2VrrexqYIFrqN+jW6Xgb2vnVWAUEY2HiEpORU0bEJSZmpGVlguflFkjXySCBlapUn1XVmlDWu9rrSd-qeVtJGJohWNTaU9s1mJz6Gw+ZxmEYzEDBeZhJaRVZZdbJDgEISCIgicTFY5KFTnTSXRAjWyUVydME+Gw2Or2XpEzx-cHeZy6WzU7zgyHQ0KLaJRZZrAAimB4RHY6Uy2TyhWxpTxFQJoGqVm8gypTxqDzBKrcdNqN3aP2c3ls7k8HX8gShcx54WW-LUiOFovF2yl+ykRzl5XUiu0Xxs+koTNcZl02uZHz6NRqDW89hq3k1xtaSZsXJtCzt8IFTpFYrYAGU6DldgBjN1wGQ407y31VL4eUlPFq6TUTNu0z4ILreSj6VU2Zx1AwOVwZkJZuErXNxZ0F4WwMt0HgQPgVyVVkonM4Khu1cOWBNWZwPEbSFnvPW-E9uGwmtweLwsicw3n2hFz-PsEiojHozEJGrb18X3Y0alJTx9FBGoXhaHpuwHX4TQ6Ycw08FwwVfW1pwdGJEXiDQADMRWERdl1XVJiNIvg6AAI1EUVgJ3OsLiVRBfCDQNhl0e97A7TU9QAWkBSwBxNB5-h+B8zGwqc+U-BJqJ4MjMCXFcIFSTIyzAdBaIYpjt1xH02P9WppAvax9ATfRT30AEVT1ezBiaZMT1NKlLVmSdYQU2dKAABTAVByCyNgIA0MBKGwHJRAAayi7l5I-fygpC2IEBi0RlwVatmOM0DCQQNxBgs2NKXBPiuz6Hw+3uDx7MDDxqTk3yUsdOI0tCqA2F4HhGMoRR0DoIgiMY4RKCStqcw6wLgu6zLUFinLfTyozaxMv1qhKyhfGcN5WQTPjnD1NsxMqlw2iHTpWvfGb8LiYtSwAMToTAkk02giB4Fg+FgEty0rWB8o2wr2IQZlnF208bGkE9bImYFTpZUk7hh946mkVVbuzGdZqesBXveyA2AAQQMogQd3esippZCseg2CWhPRw9V7ftBzQ0dMNkyFUCxeATimu6iBAvciqEwZAXDAw7HsGwXA6GxhO+X57xNBwsbMWzbBx3DaAYZh0D6ArxfB+9Gm8dwWk6WCagvKxhI5SgLJeJW3DMQMsKtYXcbw2IxZp8Gm18N5TW+YYLyjRAhJBRo7JGbwOkeFV7D1vzZsSZJA9M7bLFDuGmrtqO9RjPtXhcdxDusqxph9zNprxh7KHnE3QbNsy2UoQMk45MEBOq8wnF25pmhZQF-mT9P2ubwjUBIlTyI0nOtqJTVfn+XveOZWHVSc2HdreIcRww0Np-utYuoDljNv3SNu-tt4WkjkFle7VX+xadoldBcFdHPpuawCZEw+ivfcZdy7dAeEme2zQqSnWaN3C6-dnDXTrgEIAA */
        id: 'InlineTextEdit',
        initial: 'ReadOnly',
        context: {
            originalValue: initialValue,
            currentValue: initialValue,
        },
        states: {
            ReadOnly: {
                on: {
                    'Enter edit mode': {
                        target: 'Editing',
                    },
                },
            },

            Editing: {
                initial: 'Clean',
                states: {
                    Clean: {
                        on: {
                            'Change value': [
                                {
                                    guard: {
                                        type: 'hasChanges',
                                        params: ({ event }) => ({ value: event.value }),
                                    },

                                    target: 'Dirty',
                                    actions: assign({
                                        currentValue: ({ event }) => event.value,
                                    }),
                                },
                                {
                                    actions: assign({
                                        currentValue: ({ event }) => event.value,
                                    }),
                                },
                            ],
                            'Exit edit mode': {
                                target: '#InlineTextEdit.ReadOnly',
                            },
                        },
                    },

                    Dirty: {
                        on: {
                            'Change value': [
                                {
                                    guard: {
                                        type: 'hasChanges',
                                        params: ({ event }) => ({ value: event.value }),
                                    },
                                    actions: assign({
                                        currentValue: ({ event }) => event.value,
                                    }),
                                },
                                {
                                    target: 'Clean',
                                    actions: assign({
                                        currentValue: ({ event }) => event.value,
                                    }),
                                },
                            ],
                            'Save changes': {
                                target: 'Pending',
                            },
                            'Exit edit mode': {
                                target: '#InlineTextEdit.ReadOnly',
                                actions: 'resetValue',
                            },
                        },
                    },

                    Pending: {
                        invoke: {
                            src: 'saveChanges',
                            input: ({ context }) => context.currentValue,
                            onDone: {
                                target: '#InlineTextEdit.ReadOnly',
                                actions: assign({
                                    originalValue: ({ context }) => context.currentValue,
                                }),
                            },
                            onError: {
                                target: 'SaveFailed',
                            },
                        },
                    },

                    SaveFailed: {
                        on: {
                            'Save changes': {
                                target: 'Pending',
                            },
                            'Exit edit mode': {
                                target: '#InlineTextEdit.ReadOnly',
                                actions: 'resetValue',
                            },
                        },
                    },
                },
            },
        },
    });
}
